import React, { useState } from 'react';
import { Button } from '../Button';
import { EditIcon, DeleteIcon, PlusIcon, TrophyIcon } from './DashboardIcons';

interface Story {
  id: number;
  country: string;
  flag: string;
  fineType: string;
  result: 'ANULADA' | 'REDUCIDA';
  quote: string;
}

const initialStories: Story[] = [
  { id: 1, country: `Francia`, flag: `🇫🇷`, fineType: `Exceso de Velocidad`, result: 'ANULADA', quote: `Recibí una multa de un radar en Francia y no sabía qué hacer. Multas.EU se encargó de todo...` },
  { id: 2, country: `España`, flag: `🇪🇸`, fineType: `Aparcamiento en zona SER`, result: 'ANULADA', quote: `Me multaron en Madrid supuestamente por no tener ticket. Envié mi caso y ganaron el recurso.` },
  { id: 3, country: `Italia`, flag: `🇮🇹`, fineType: `Acceso a Zona de Tráfico Limitado (ZTL)`, result: 'REDUCIDA', quote: `Entré en una ZTL en Florencia. La multa era altísima. Consiguieron una reducción del 50%.` },
];

const emptyStory: Story = { id: 0, country: ``, flag: ``, fineType: ``, result: 'ANULADA', quote: `` };

export const SuccessStoriesView: React.FC = () => {
    const [stories, setStories] = useState<Story[]>(initialStories);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingStory, setEditingStory] = useState<Story | null>(null);

    const handleOpenForm = (story: Story | null = null) => {
        setEditingStory(story ? { ...story } : { ...emptyStory, id: Date.now() });
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingStory(null);
    };

    const handleSave = () => {
        if (!editingStory) return;

        if (stories.some(s => s.id === editingStory.id)) {
            // Edit
            setStories(stories.map(s => s.id === editingStory.id ? editingStory : s));
        } else {
            // Add
            setStories([...stories, editingStory]);
        }
        handleCloseForm();
    };
    
    const handleDelete = (id: number) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este caso de éxito?')) {
             setStories(stories.filter(s => s.id !== id));
        }
    };


    return (
        <div className="p-8 bg-white rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center"><TrophyIcon /> <span className="ml-2">Gestión de Casos de Éxito</span></h1>
                <Button onClick={() => handleOpenForm()} variant="primary" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <span className="flex items-center"><PlusIcon /> <span className="ml-2">Añadir Caso</span></span>
                </Button>
            </div>
            
             {isFormOpen && editingStory && (
                <div className="bg-gray-50 p-6 rounded-lg mb-6 border border-gray-200">
                    <h2 className="text-xl font-semibold mb-4">{editingStory.id === 0 ? 'Nuevo Caso de Éxito' : 'Editando Caso de Éxito'}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="text" placeholder="País (ej. Francia)" value={editingStory.country} onChange={e => setEditingStory({...editingStory, country: e.target.value})} className="w-full p-2 border rounded" />
                        <input type="text" placeholder="Emoji Bandera (ej. 🇫🇷)" value={editingStory.flag} onChange={e => setEditingStory({...editingStory, flag: e.target.value})} className="w-full p-2 border rounded" />
                        <input type="text" placeholder="Tipo de Multa" value={editingStory.fineType} onChange={e => setEditingStory({...editingStory, fineType: e.target.value})} className="w-full p-2 border rounded" />
                        <select value={editingStory.result} onChange={e => setEditingStory({...editingStory, result: e.target.value as 'ANULADA' | 'REDUCIDA'})} className="w-full p-2 border rounded bg-white">
                            <option value="ANULADA">Anulada</option>
                            <option value="REDUCIDA">Reducida</option>
                        </select>
                        <textarea placeholder="Testimonio / Cita" value={editingStory.quote} onChange={e => setEditingStory({...editingStory, quote: e.target.value})} className="w-full p-2 border rounded md:col-span-2" rows={3}></textarea>
                    </div>
                    <div className="flex justify-end gap-3 mt-4">
                        <Button onClick={handleCloseForm} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-200">Cancelar</Button>
                        <Button onClick={handleSave} variant="success">Guardar Cambios</Button>
                    </div>
                </div>
            )}


            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th className="px-6 py-3">País</th>
                            <th className="px-6 py-3">Tipo Multa</th>
                            <th className="px-6 py-3">Resultado</th>
                            <th className="px-6 py-3">Cita</th>
                            <th className="px-6 py-3 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stories.map(story => (
                            <tr key={story.id} className="bg-white border-b hover:bg-gray-50">
                                <td className="px-6 py-4 font-medium text-gray-900">{story.flag} {story.country}</td>
                                <td className="px-6 py-4">{story.fineType}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-1 text-xs font-bold text-white rounded-full ${story.result === 'ANULADA' ? 'bg-green-500' : 'bg-yellow-500'}`}>{story.result}</span>
                                </td>
                                <td className="px-6 py-4 truncate max-w-xs">{story.quote}</td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end space-x-3">
                                        <button onClick={() => handleOpenForm(story)} className="text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                                        <button onClick={() => handleDelete(story.id)} className="text-red-500 hover:text-red-700"><DeleteIcon /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-8 border-t pt-6">
                 <h2 className="text-xl font-bold text-gray-800 mb-2">Ajuste del Multómetro</h2>
                 <p className="text-sm text-gray-600 mb-4">Controla el valor mostrado en el "Multómetro" de la tasa de éxito. El valor debe estar entre 0 y 100.</p>
                 <div className="flex items-center gap-4">
                    <label htmlFor="multometro" className="font-semibold">Tasa de Éxito (%):</label>
                    <input type="range" id="multometro" name="multometro" min="0" max="100" defaultValue="95" className="w-64" />
                    <span className="font-bold text-lg text-blue-600">95%</span>
                    <Button variant="success">Actualizar Multómetro</Button>
                 </div>
            </div>
        </div>
    );
};